import { Radio, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box, Text } from '@src/shared/ui';
import React from 'react';
import { Recommended } from '../recommended';
import { LoanSimulation } from '@src/shared/models';
import { formatBR } from '@src/shared/utils';
import { useLoanSimulationView } from './hooks';
import { colors } from '@src/config/theme/tokens';

const CustomRadio = styled(Radio)({
  color: colors.primary30,
  '.MuiTouchRipple-root': {
    display: 'none',
  },
});

type LoanSimulationViewProps = {
  className?: string;
  loading?: boolean;
  selected?: boolean;
  onClick?: VoidFunction;
  loanSimulation: LoanSimulation;
};

export const LoanSimulationView: React.FC<LoanSimulationViewProps> = ({
  className,
  loanSimulation,
  selected: customSelected,
  onClick,
  loading,
}) => {
  const { selected, isSelected, downPaymentPlusInstallment } =
    useLoanSimulationView({
      loanSimulation,
      customSelected,
    });

  const seletedClass =
    isSelected && !loading
      ? 'border-primary-30'
      : 'border-neutral-30 md:hover:border-neutral-40';

  const loadingText = () => (
    <Skeleton variant='rounded' height={24} className='min-w-full' />
  );

  return (
    <Box
      className={`
      col-span-4
      border-[1px]
      ${seletedClass}
      rounded-2xl
      col-span-4
      min-w-full
      flex
      justify-start
      flex-col
      pr-6

      sm:pr-0
      sm:col-span-8
      sm:flex-row
      sm:justify-between

      md:col-span-6

      ${className}
      `}
      onClick={() => {
        if (onClick) onClick();

        selected();
      }}
    >
      <Recommended loading={loading} show={loanSimulation?.recommended} />
      <Box
        className={`
          flex
          flex-1
          p-6
          flex-row
          min-w-full
          sm:min-w-min
          pr-0
        `}
      >
        {loading ? (
          <Skeleton
            variant='circular'
            width={24}
            height={24}
            className='mr-2'
          />
        ) : (
          <CustomRadio
            checked={isSelected}
            className='m-0 p-0 self-start pr-2'
          />
        )}
        <Box
          className={`
          flex
          flex-col
          float-start
          ${loading ? 'gap-3 w-full' : ''}
        `}
        >
          {loading ? (
            loadingText()
          ) : (
            <Text
              color='secondary50'
              className={`
                max-[356px]:text-[14px]
                text-base
                text-left
                tracking-normal
                xxs:text-base

                sm:text-lg
              `}
            >
              {downPaymentPlusInstallment}
            </Text>
          )}
          {loading ? (
            loadingText()
          ) : (
            <Text
              color='neutral50'
              className={`
                mt-1
                max-[356px]:text-[12px]
                text-[14px]
                tracking-normal
                self-start

                sm:text-base
              `}
            >
              Total: {formatBR(loanSimulation?.totalAmountOwedWithDownPayment)}
            </Text>
          )}
        </Box>
      </Box>
      <Recommended sm loading={loading} show={loanSimulation?.recommended} />
    </Box>
  );
};
